import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Pencil, Trash2 } from "lucide-react";
import { Button, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import {
  useAddTaskComment,
  useDeleteTaskComment,
  useTaskComments,
  useUpdateTaskComment,
} from "../hooks/useWorkTasks";
import { MentionTextarea } from "./MentionTextarea";
import type { TaskCommentDto } from "@/api/workTasks";
import { useDateFormatter } from "@/utils/datetime";
import { ApiError } from "@/api/client";

interface Props {
  taskId: string;
}

export function TaskCommentsThread({ taskId }: Props) {
  const { user, hasPermission } = useAuth();
  const isAdmin = hasPermission("Tasks.ViewAll");
  const list = useTaskComments(taskId);
  const add = useAddTaskComment(taskId);
  const update = useUpdateTaskComment(taskId);
  const del = useDeleteTaskComment(taskId);
  const { toast } = useToast();

  const [draft, setDraft] = useState("");

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    try {
      await add.mutateAsync(body);
      setDraft("");
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not add comment.";
      toast({ message: msg, tone: "error" });
    }
  };

  const comments = (list.data ?? []).filter((c) => !c.isDeleted);

  return (
    <div className="flex flex-col gap-space-4">
      <h3 className="font-display font-bold text-h3 text-onyx m-0">Comments</h3>

      {list.isLoading && (
        <div className="text-small text-gray-mid">Loading comments…</div>
      )}

      {comments.length === 0 && !list.isLoading && (
        <div className="text-small text-gray-mid">
          No comments yet. Be the first to add context.
        </div>
      )}

      <ol className="flex flex-col gap-space-3 m-0 p-0 list-none">
        {comments.map((c) => (
          <CommentRow
            key={c.id}
            comment={c}
            canEdit={c.authorId === user?.id}
            canDelete={c.authorId === user?.id || isAdmin}
            onEdit={(content) =>
              update.mutateAsync({ commentId: c.id, content }).catch((err: unknown) => {
                const msg =
                  err instanceof ApiError
                    ? err.errors?.[0] ?? err.message
                    : "Could not update.";
                toast({ message: msg, tone: "error" });
              })
            }
            onDelete={() =>
              del.mutateAsync(c.id).catch((err: unknown) => {
                const msg =
                  err instanceof ApiError
                    ? err.errors?.[0] ?? err.message
                    : "Could not delete.";
                toast({ message: msg, tone: "error" });
              })
            }
          />
        ))}
      </ol>

      <div className="flex flex-col gap-space-2">
        <MentionTextarea
          value={draft}
          onChange={setDraft}
          placeholder="Add a comment. Markdown is supported. Type @ to mention a teammate."
          aria-label="Add a comment"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || add.isPending}
            loading={add.isPending}
          >
            Post comment
          </Button>
        </div>
      </div>
    </div>
  );
}

function CommentRow({
  comment,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}: {
  comment: TaskCommentDto;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (content: string) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.content);
  const { dateTime } = useDateFormatter();

  const save = async () => {
    const body = draft.trim();
    if (!body) return;
    await onEdit(body);
    setEditing(false);
  };

  return (
    <li className="rounded-md border border-border-light bg-white-pure p-space-3">
      <div className="flex items-center justify-between mb-space-2">
        <div className="text-label text-gray-mid">
          <span className="font-semibold text-onyx">{comment.authorDisplayName}</span> ·{" "}
          {dateTime(comment.createdAt)}
          {comment.modifiedAt && comment.modifiedAt !== comment.createdAt && (
            <span className="ml-space-2 text-gray-mid">(edited)</span>
          )}
        </div>
        <div className="flex items-center gap-space-1">
          {canEdit && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="p-space-1 rounded-md text-gray-mid hover:text-onyx"
              aria-label="Edit comment"
            >
              <Pencil size={14} aria-hidden />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="p-space-1 rounded-md text-gray-mid hover:text-status-error"
              aria-label="Delete comment"
            >
              <Trash2 size={14} aria-hidden />
            </button>
          )}
        </div>
      </div>
      {editing ? (
        <div className="flex flex-col gap-space-2">
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} />
          <div className="flex justify-end gap-space-2">
            <Button variant="secondary" size="sm" type="button" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" type="button" onClick={save} disabled={!draft.trim()}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="prose prose-sm max-w-none text-onyx text-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{comment.content}</ReactMarkdown>
        </div>
      )}
    </li>
  );
}
